import moment, {Moment} from 'moment-timezone'
import {mmap} from '@freckle/maybe'
import {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import {RlSkillUspIdT} from '@freckle/educator-entities/ts/common/types/rl-skill-usp-id'
import {ElaAdaptiveSkillAssignmentPathwayT} from '@freckle/educator-entities/ts/ela/adaptive-skills/models/ela-adaptive-skill-assignment-pathway'
import {DehydratedSkill} from './../../../common/models/rl-skill'
import {RlStandardWithDomainT} from './../../../common/models/rl-standard'
import {Metadata} from './metadata'
import {fromOpenApiSkill} from './skill'

/**
 * ELA Assessments
 */

export type ElaAssessmentMetadataT = {
  assessmentContentUuid: string
}

/**
 * ELA Decodables
 */

export type ElaDecodablesMetadataT = {
  decodableUuid: string
}

/**
 * ELA Adaptive Skills
 */

export type ElaAdaptiveSkillsPracticeMetadataT = {
  domain: RlDomainT | undefined | null
  rlStandard: RlStandardWithDomainT | undefined | null
  rlSkillId: RlSkillUspIdT | undefined | null
  startsAt: Moment | undefined | null
  pathway: ElaAdaptiveSkillAssignmentPathwayT | undefined | null
}

export function fromOpenApiElaAdaptiveSkillsPracticeMetadata(
  x: Metadata<'ela_adaptive_skills_practice'>
): ElaAdaptiveSkillsPracticeMetadataT {
  const {rlSkillId, pathway, rlStandard} = x
  return {
    ...x,
    rlStandard,
    rlSkillId,
    pathway,
    startsAt: mmap(moment, x.startsAt),
  }
}

/**
 * ELA Targeted Skills
 */

export type ElaTargetedSkillsPracticeMetadataT = {
  standard: RlStandardWithDomainT
  skill: DehydratedSkill
  startsAt: Moment | undefined | null
}

export function fromOpenApiElaTargetedSkillsPracticeMetadata(
  x: Metadata<'ela_targeted_skills_practice'>
): ElaTargetedSkillsPracticeMetadataT {
  const {standard, skill} = x
  if (standard === null) {
    throw new Error('ElaTargetedSkillsPractice session metadata: standard is null')
  }
  if (skill === null) {
    throw new Error('ElaTargetedSkillsPractice session metadata: skill is null')
  }
  return {
    ...x,
    standard,
    skill: fromOpenApiSkill(skill),
    startsAt: mmap(moment, x.startsAt),
  }
}

/**
 * ELA Focus Skills
 */

// TODO: We are using the tag 'ela_targeted_skills_practice'
// for both targeted and focus skills practice since when the session is created on the student side,
// that's how it was tagged. Tech debt task: https://app.asana.com/0/1201335092255974/1207360706468057/f
export type ElaFocusSkillsPracticeMetadataT = {
  startsAt: Moment | undefined | null
}

export function fromOpenApiElaFocusSkillsPracticeMetadata(
  x: Metadata<'ela_targeted_skills_practice'>
): ElaFocusSkillsPracticeMetadataT {
  return {
    ...x,
    startsAt: mmap(moment, x.startsAt),
  }
}

/**
 * ELA Word Study
 */

type ElaWordStudyAssignmentTypeT =
  | 'word_study_assignment_adaptive'
  | 'word_study_assignment_targeted'

export type ElaWordStudyMetadataT = {
  assignmentType: ElaWordStudyAssignmentTypeT
  rlStandard: RlStandardWithDomainT | undefined | null
  rlSkill: DehydratedSkill | undefined | null
}

export function fromOpenApiElaWordStudyMetadata(
  x: Metadata<'ela_word_study'>
): ElaWordStudyMetadataT {
  const {rlStandard} = x
  return {
    ...x,
    rlStandard,
    rlSkill: mmap(fromOpenApiSkill, x.rlSkill),
  }
}

/**
 * ELA Sight Words
 */

export type ElaSightWordsMetadataT = {
  level: number
}
